import { federation } from "@module-federation/vite";
import { nxViteTsPaths } from "@nx/vite/plugins/nx-tsconfig-paths.plugin";
import type { Exposes, IrisAppManifest, Shared } from "@trackunit/iris-app-api";
import {
  CopyPattern,
  createInvokeProxyMiddleware,
  enableTsConfigPath,
  getAvailablePort,
  getCopyPatterns,
  getExposedExtensions,
  getSharedDependencies,
  injectFullDescriptionImages,
  logInfo,
  ServersidePortMap,
  updateCustomFields,
  updateExtensions,
  updateIndexHtml,
} from "@trackunit/iris-app-build-utilities";
import react from "@vitejs/plugin-react-swc";
import * as fs from "node:fs";
import type { IncomingMessage, ServerResponse } from "node:http";
import * as path from "node:path";
import { mergeConfig, type PluginOption, type ResolvedConfig, type UserConfig, type ViteDevServer } from "vite";
import checker from "vite-plugin-checker";
import { createCustomFieldsDegradedReminderPlugin } from "./customFieldsDegradedReminderPlugin";
import { logEvent, logTiming, shortenPath, timeAsync, wrapResponseTiming } from "./devServerTiming";
import { createExtensionLoaderCache } from "./extensionLoaderCache";
import { loadCssInjectedByJsPlugin } from "./loadCssInjectedByJsPlugin";
import { createManifestCache } from "./manifestCache";
import { loadManifest } from "./manifestLoader";

// Only import enableTsConfigPath statically - other utilities are dynamically imported
// AFTER tsconfig paths are registered to avoid oxc analysis warnings

// MIME types for serving extension assets during development
const MIME_TYPES: Record<string, string> = {
  ".svg": "image/svg+xml",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".ico": "image/x-icon",
};

const getMimeType = (filePath: string): string =>
  MIME_TYPES[path.extname(filePath).toLowerCase()] || "application/octet-stream";

/**
 * Resolves a URL path like "/{extensionId}/assets/icon.svg" to the actual file path on disk.
 * Returns null if no matching extension is found or the file doesn't exist.
 */
const resolveExtensionAsset = (url: string, manifest: IrisAppManifest, workspaceRoot: string): string | null => {
  for (const ext of manifest.extensions) {
    if (url.startsWith(`/${ext.id}/`)) {
      const assetPath = url.substring(ext.id.length + 2);
      const filePath = path.join(workspaceRoot, ext.sourceRoot, assetPath);

      if (fs.existsSync(filePath) && fs.statSync(filePath).isFile()) {
        return filePath;
      }
    }
  }
  return null;
};

interface TrackunitIrisAppVitePluginOptions {
  appDir: string;
  /** Path to the workspace root (where tsconfig.base.json is located) */
  workspaceRoot?: string;
  serversidePortMap?: ServersidePortMap;
  config: UserConfig;
  skipTypeChecking?: boolean;
  /** When true, register the plugin that re-warns about the skipped custom-field check on each rebuild. */
  degraded?: boolean;
}

interface ViteSharedConfig {
  [key: string]: {
    singleton?: boolean;
    eager?: boolean;
    requiredVersion?: string;
    /** When false, the shared dependency is NOT bundled - it's loaded from the host at runtime */
    import?: false;
  };
}

interface ViteExposesConfig {
  [key: string]: string;
}

// Flag to track if tsconfig paths have been registered
let tsconfigPathsRegistered = false;

/**
 * Registers tsconfig paths for resolving @trackunit/* imports at runtime.
 * This is needed because the manifest file imports from @trackunit/* packages.
 */
const registerTsConfigPaths = (workspaceRoot: string, appDir: string): void => {
  if (tsconfigPathsRegistered) {
    return;
  }

  enableTsConfigPath({ projectRootDir: appDir, workspaceRoot });
  tsconfigPathsRegistered = true;
};

/**
 * Converts the shared dependencies from webpack format to Vite module federation format.
 * Handles both array and object variants of the Shared type.
 *
 * NOTE: We do NOT set `import: false` here because iris apps run in an iframe (separate
 * JavaScript context). The host's shared dependencies are NOT accessible across the iframe
 * boundary, so the iris app must bundle its own copies as a fallback. The `singleton: true`
 * config ensures that within the iframe context, only one instance is used.
 */
const convertSharedToViteFormat = (shared: Shared): ViteSharedConfig => {
  const result: ViteSharedConfig = {};

  const processItem = (item: unknown): void => {
    if (typeof item === "string") {
      // Bundle this dependency - iris apps need their own copy since they run in an iframe
      result[item] = {};
    } else if (item !== null && typeof item === "object") {
      for (const [key, value] of Object.entries(item)) {
        const baseConfig = typeof value === "string" ? {} : (value ?? {});
        // Remove import: false if it was set - we need to bundle shared deps for iframe isolation
        const { import: _import, ...restConfig } = baseConfig;
        result[key] = restConfig;
      }
    }
  };

  // Handle both array and object variants of Shared type
  if (Array.isArray(shared)) {
    for (const item of shared) {
      processItem(item);
    }
  } else {
    processItem(shared);
  }
  // For now we do not use any shared for vite build - as it create random crashes, it seemed like react was not garanteed to be loaded
  return {};
  // return result;
};

/**
 * Converts the webpack Exposes type to Vite's expected format.
 * getExposedExtensions returns an object with string values, but the Exposes type
 * is a union that includes arrays. This extracts just the object entries.
 *
 */
const convertExposesToViteFormat = (exposes: Exposes): ViteExposesConfig => {
  const result: ViteExposesConfig = {};

  if (typeof exposes === "object" && !Array.isArray(exposes)) {
    for (const [key, value] of Object.entries(exposes)) {
      if (typeof value === "string") {
        result[key] = value;
      }
    }
  }

  return result;
};

const generateIndexHtml = ({
  manifest,
  packageJson,
  options,
}: {
  manifest: IrisAppManifest;
  packageJson: string;
  options: TrackunitIrisAppVitePluginOptions;
}): string => {
  // Register tsconfig paths to load the utility
  registerTsConfigPaths(options.workspaceRoot ?? options.appDir, options.appDir);

  // Only include dev scripts and React DevTools connection in development
  const iframeRedirect =
    options.config.mode === "development"
      ? `<script nonce="{{nonce}}">
    if (window.self === window.top) { window.location.replace("https://new.manager.trackunit.com/goto/iris-app-dev"); }
  </script>`
      : "";

  const devtools =
    options.config.mode === "development"
      ? `<script nonce="{{nonce}}" defer src="http://localhost:8097"></script>
      ${iframeRedirect}
      <script nonce="{{nonce}}">
    if (typeof global === 'undefined') { window.global = window; }
    if (typeof process === 'undefined') { window.process = { env: {} }; }
  </script>
`
      : `
<script nonce="{{nonce}}">
    if (typeof global === 'undefined') { window.global = window; }
    if (typeof process === 'undefined') { window.process = { env: {} }; }
  </script>`;

  return updateIndexHtml({ manifest, packageJson, devtools });
};

/**
 * Vite plugin that generates manifest.json, package.json, and index.html for Iris Apps.
 * This is the Vite equivalent of TrackunitIrisAppWebpackPlugin.
 *
 * Returns an array of plugins including Module Federation configuration.
 */
export async function getTrackunitIrisAppVitePlugins(
  options: TrackunitIrisAppVitePluginOptions
): Promise<Array<PluginOption>> {
  const pluginConstructionStart = performance.now();
  logEvent("plugin construction: started");

  const resolvedAppDir = path.resolve(options.appDir);
  const workspaceRoot = options.workspaceRoot ?? path.resolve(options.appDir, "../..");

  // ESM-only since v5 (Vite 8). Load via file URL — Nx CJS executor hooks cannot
  // resolve the package's import-only exports map.
  const cssInjectedByJsPlugin = await loadCssInjectedByJsPlugin(workspaceRoot);

  // Load manifest upfront for federation config
  const initialManifest = loadManifest(workspaceRoot, resolvedAppDir);

  // Get build utilities (tsconfig paths now registered via loadManifest)

  // Get an available port in the Iris app port range
  const port = await timeAsync("getAvailablePort", () => getAvailablePort(22220, 22229));

  // Get module federation configuration from manifest
  const webpackExposes = await timeAsync(`getExposedExtensions (${initialManifest.extensions.length} extensions)`, () =>
    getExposedExtensions({
      nxRootDir: workspaceRoot,
      manifest: initialManifest,
    })
  );
  const shared = getSharedDependencies({ manifest: initialManifest });
  const viteExposes = convertExposesToViteFormat(webpackExposes);
  const viteShared = convertSharedToViteFormat(shared);
  const hasExposes = Object.keys(viteExposes).length > 0;

  // Only create the federation plugin when there are modules to expose.
  // @module-federation/vite deadlocks during build when exposes is empty because
  // its proxyRemoteEntry plugin waits for a module-parse promise that never resolves.
  let federationPlugin: PluginOption | null = null;
  if (hasExposes) {
    federationPlugin = federation({
      name: initialManifest.moduleFederationName,
      filename: "remoteEntry.js",
      library: { type: "global", name: initialManifest.moduleFederationName },
      exposes: viteExposes,
      shared: viteShared,
      remotes: {},
      manifest: false,
      dts: false,
      shareStrategy: "loaded-first",
      getPublicPath: `return (function() {
        var base;
        if (typeof import.meta !== 'undefined' && import.meta.url) {
          base = new URL('./', import.meta.url).href;
        } else if (typeof document !== 'undefined' && document.currentScript) {
          base = new URL('./', document.currentScript.src).href;
        } else {
          base = document.baseURI;
        }
        return base;
      })()`,
    });
  }

  // Conditionally create bundle analyzer plugin (same pattern as webpack/rspack)
  // Created here so we have access to the output directory path
  let bundleAnalyzerPlugin: PluginOption | null = null;
  if (process.env.BUNDLE_ANALYSE === "true") {
    // Compute output directory: dist/apps/{app-name}
    const appName = path.basename(resolvedAppDir);
    const statsPath = path.join(workspaceRoot, "dist", "apps", appName, "stats.html");

    // Dynamic ESM import for optional dependency
    const { visualizer } = await import("rollup-plugin-visualizer");
    bundleAnalyzerPlugin = visualizer({
      filename: statsPath,
      template: "treemap",
      gzipSize: true,
      brotliSize: true,
    });

    // eslint-disable-next-line no-console
    console.log(`\n📊 Bundle analyzer enabled - stats.html will be created at:\n   ${statsPath}\n`);
  }

  // Plugin to disable hostAutoInit for pure remotes
  // The @module-federation/vite plugin always injects a hostAutoInit module
  // that auto-initializes the federation runtime. For pure remotes (Iris Apps),
  // we don't need this - the host handles initialization. This plugin intercepts
  // the virtual module and returns an empty module to prevent the build error.
  const disableHostAutoInitPlugin: PluginOption = {
    name: "disable-host-auto-init",
    enforce: "pre",
    resolveId(id) {
      // Match the hostAutoInit virtual module pattern: __H_A_I__hostAutoInit__H_A_I__
      if (id.includes("__H_A_I__hostAutoInit__H_A_I__")) {
        return "\0virtual:empty-host-auto-init";
      }
      return null;
    },
    load(id) {
      if (id === "\0virtual:empty-host-auto-init") {
        // Return empty module - no auto-initialization needed for pure remotes
        return "export default {}";
      }
      return null;
    },
  };

  // Create the iris app plugin
  let config: ResolvedConfig;

  const reloadManifest = (): IrisAppManifest => {
    return loadManifest(workspaceRoot, resolvedAppDir);
  };

  const generateManifestJson = (manifestData: IrisAppManifest): IrisAppManifest => {
    // Register tsconfig paths to load utilities
    registerTsConfigPaths(workspaceRoot, resolvedAppDir);

    const manifestCopy = { ...manifestData };
    const appSrc = path.join(resolvedAppDir, "src");

    manifestCopy.customFieldDefinitions = updateCustomFields(manifestData.customFieldDefinitions, appSrc);
    manifestCopy.extensions = updateExtensions([...manifestData.extensions]);
    return injectFullDescriptionImages(manifestCopy, resolvedAppDir);
  };

  const manifestCache = createManifestCache({
    reloadManifest,
    generateManifestJson,
    initialManifest,
  });

  // The /extensionloader.js handler proxies to https://iris.trackunit.app
  // (or http://localhost:3000 when LOCAL=true). The script changes very
  // rarely, so we cache the body in memory with a TTL and dedupe concurrent
  // fetches via createExtensionLoaderCache. Set IRIS_EXTENSIONLOADER_NO_CACHE=1
  // to bypass the cache for debugging.
  const getExtensionLoaderUrl = (): string =>
    process.env.LOCAL === "true"
      ? "http://localhost:3000/extensionloader.js"
      : "https://iris.trackunit.app/extensionloader.js";

  const extensionLoaderCache = createExtensionLoaderCache({
    getUrl: getExtensionLoaderUrl,
    shouldBypassCache: () => process.env.IRIS_EXTENSIONLOADER_NO_CACHE === "1",
  });

  // No explicit type annotation to avoid excessive stack depth error with federation plugin's complex types
  const irisAppPlugin = {
    name: "trackunit-iris-app-vite-configuration",

    config(conf: UserConfig) {
      // Base config for both serve and build
      const baseConfig: UserConfig = {
        // Use empty base for runtime URL resolution - assets will use relative paths
        base: "",
        // Runtime URL resolution - equivalent to rspack's publicPath: "auto"
        // Uses document.currentScript to determine base URL at runtime
        experimental: {
          renderBuiltUrl(filename, { hostType }) {
            // CSS files cannot execute JavaScript runtime expressions - use relative paths instead
            // This applies to fonts, images, and other assets referenced via url() in CSS
            if (hostType === "css") {
              return { relative: true };
            }
            // For JS/HTML, use runtime resolution using document.currentScript (like webpack/rspack's publicPath: "auto")
            return {
              runtime: `((function() {
                return document.baseURI + ${JSON.stringify(filename)};
              })())`,
            };
          },
        },
        // PostCSS configuration for Tailwind CSS v4
        // Using @tailwindcss/postcss instead of @tailwindcss/vite for better monorepo support.
        css: {
          postcss: {
            plugins: [require("@tailwindcss/postcss")({})],
          },
        },
        server: {
          port,
          strictPort: true,
          host: "localhost",
          // Enable CORS for cross-origin requests from new.manager.trackunit.com
          cors: {
            origin: true, // Reflect request origin (supports all origins)
            credentials: true,
            methods: ["GET", "POST", "PUT", "DELETE", "OPTIONS"],
            allowedHeaders: [
              "X-Requested-With",
              "baggage",
              "content-type",
              "Authorization",
              "sentry-trace",
              "session-id",
              "commit-number",
              "x-trackunitappversion",
            ],
            maxAge: 3600,
          },
        },
        // Production build optimization
        build: {
          // Vite 8's default minifier. "esbuild" would require esbuild as a separate
          // install, which generated Iris app workspaces do not have.
          minify: "oxc",
          // CSS code splitting is required for relativeCSSInjection to work
          // (cssInjectedByJsPlugin enforces cssCodeSplit: true when relativeCSSInjection is enabled)
          // Each chunk's CSS is injected when that chunk loads - ideal for Module Federation
          // NOTE: We don't set cssCodeSplit here because the plugin controls it
          cssCodeSplit: true,
          rollupOptions: {
            output: {
              // Chunk splitting strategy - bundle small modules together to reduce HTTP requests
              manualChunks(id: string) {
                // Skip Module Federation virtual modules and runtime - let MF handle these
                if (
                  id.includes("__mf__virtual") ||
                  id.includes("@mf-types") ||
                  id.includes("virtualExposes") ||
                  id.includes("virtual_mf-exposes") ||
                  id.includes("remoteEntry") ||
                  id.includes("_mf_")
                ) {
                  return undefined;
                }

                // Skip node_modules - let Rollup handle vendor chunking
                if (id.includes("node_modules")) {
                  return undefined;
                }

                // 1. Split translations per language (like Rspack's splitChunks cacheGroups)
                const translationMatch = id.match(/[\\/]locales[\\/]([^/\\]+)[\\/]translation\.json$/);
                if (translationMatch) {
                  return `translations-${translationMatch[1]}`;
                }

                // Let Rollup handle everything else - default chunking is fine
                // Vite/Rollup will merge small chunks automatically
                return undefined;
              },
            },
          },
        },
      };

      return mergeConfig(conf, baseConfig, false); // false = don't concatenate root-level arrays
    },

    configResolved(resolvedConfig: ResolvedConfig) {
      config = resolvedConfig;
    },
    buildStart() {
      manifestCache.reloadManifestSync();
    },

    configureServer(server: ViteDevServer) {
      // Watch iris-app-manifest.ts and invalidate the manifest cache on change so
      // HMR picks up edits (e.g. adding/removing extensions). All other paths
      // serve from the cache populated by the first request.
      const manifestFilePath = path.join(resolvedAppDir, "iris-app-manifest.ts");
      server.watcher.add(manifestFilePath);
      server.watcher.on("change", changedPath => {
        if (path.normalize(changedPath) === path.normalize(manifestFilePath)) {
          manifestCache.invalidateManifestCache();
          logEvent("manifest cache: invalidated (iris-app-manifest.ts changed)");
        }
      });

      // Request timing middleware - MUST be registered before any other middleware
      // so we observe the full server-side response time. Logs to stdout when
      // the response takes longer than TRANSFORM_LOG_THRESHOLD_MS. Enable with
      // IRIS_TIMING=true.
      server.middlewares.use((req: IncomingMessage, res: ServerResponse, next: () => void) => {
        wrapResponseTiming(req, res);
        next();
      });

      // Route /invoke requests to local serverside extensions when available.
      server.middlewares.use("/invoke", createInvokeProxyMiddleware(options.serversidePortMap));

      // Serve extension assets from source during development (/{extensionId}/... → {sourceRoot}/...)
      server.middlewares.use((req: IncomingMessage, res: ServerResponse, next: () => void) => {
        const url = req.url?.split("?")[0] ?? "/";
        // Use the in-memory manifest if available; fall back to initialManifest
        // (loaded once at plugin construction) so this synchronous middleware
        // never blocks on a slow manifest reload.
        const filePath = resolveExtensionAsset(url, manifestCache.getManifestSync() ?? initialManifest, workspaceRoot);

        if (filePath) {
          res.setHeader("Content-Type", getMimeType(filePath));
          res.end(fs.readFileSync(filePath));
          return;
        }
        next();
      });

      // Global CORS headers middleware - must be first to set headers on all responses
      server.middlewares.use((req: IncomingMessage, res: ServerResponse, next: () => void) => {
        // Set Access-Control-Allow-Origin based on request origin (needed for CORS preflight)
        const origin = req.headers.origin || "https://dev.manager.trackunit.com";
        res.setHeader("Access-Control-Allow-Origin", origin);
        res.setHeader("Access-Control-Allow-Credentials", "true");
        res.setHeader("Access-Control-Max-Age", "3600");
        res.setHeader("Access-Control-Allow-Methods", "GET, POST, PUT, DELETE, OPTIONS");
        res.setHeader(
          "Access-Control-Allow-Headers",
          "X-Requested-With, baggage, content-type, Authorization, sentry-trace, session-id, commit-number, x-trackunitappversion"
        );
        next();
      });

      // IMPORTANT: This middleware must be synchronous for requests we want to pass through to Vite.
      // Async middlewares break Connect's middleware chain when calling next().
      // We use a sync wrapper and only do async work for routes we handle ourselves.
      server.middlewares.use((req: IncomingMessage, res: ServerResponse, next: () => void) => {
        const url = req.url?.split("?")[0] ?? "/";

        // Let Vite handle its internal routes (@fs, @vite, @react-refresh, node_modules, etc.)
        // MUST call next() synchronously for these to work properly
        const isViteInternal =
          url.startsWith("/@") ||
          url.startsWith("/node_modules/") ||
          url.startsWith("/__mf__virtual/") ||
          url.startsWith("/__vite");
        if (isViteInternal) {
          next();
          return;
        }

        // CORS preflight handler - respond to OPTIONS requests
        if (req.method === "OPTIONS") {
          res.statusCode = 204;
          res.end();
          return;
        }

        // For non-GET/HEAD/OPTIONS on root path, return allowed methods
        if (req.url?.endsWith("/") && req.method !== "GET" && req.method !== "HEAD") {
          res.end("GET, HEAD");
          return;
        }

        // Serve manifest.json during development (async route - handle response ourselves)
        if (req.url === "/manifest.json") {
          void (async () => {
            try {
              const manifestJson = await manifestCache.getCachedManifestJson();

              res.setHeader("Content-Type", "application/json");
              res.end(JSON.stringify(manifestJson, null, 2));
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Error generating manifest.json:", error);
              res.statusCode = 500;
              res.end(String(error));
            }
          })();
          return; // Don't call next() - we're handling this route
        }

        // Serve package.json during development
        if (req.url === "/package.json") {
          try {
            const packageJsonPath = path.join(resolvedAppDir, "package.json");
            const packageJson = fs.readFileSync(packageJsonPath, "utf8");

            res.setHeader("Content-Type", "application/json");
            res.end(packageJson);
            return;
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error serving package.json:", error);
          }
        }

        // Serve manifestAndToken during development (async route - handle response ourselves)
        // Calls getCachedManifestJson directly instead of HTTP-fetching /manifest.json
        // to avoid a redundant manifest reload on every request (the host calls this
        // endpoint multiple times during initial app boot).
        if (req.url?.startsWith("/manifestAndToken")) {
          void (async () => {
            try {
              const manifestJson = await manifestCache.getCachedManifestJson();
              res.setHeader("Content-Type", "application/json");
              res.end(JSON.stringify({ manifest: manifestJson }));
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("ERROR: ", e);
              res.statusCode = 500;
              res.end(String(e));
            }
          })();
          return; // Don't call next() - we're handling this route
        }

        // Serve extensionloader.js during development.
        // To test extensionloader locally set LOCAL=true in front of the serve command.
        // The fetched body is cached in memory; see createExtensionLoaderCache.
        if (req.url?.startsWith("/extensionloader.js")) {
          void (async () => {
            try {
              const body = await extensionLoaderCache.getCachedExtensionLoaderBody();
              res.setHeader("Content-Type", "application/javascript");
              res.end(body);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("ERROR: ", e);
              res.statusCode = 500;
              res.end(String(e));
            }
          })();
          return; // Don't call next() - we're handling this route
        }

        // Serve generated index.html for root path and SPA fallback routes
        // This handles the case where no physical index.html exists (async route - handle response ourselves)
        if (req.method === "GET") {
          const acceptsHtml = req.headers.accept?.includes("text/html");
          const hasExtension = /\.\w+$/.test(url);
          const isRootOrSpaRoute = url === "/" || url === "/index.html" || (!hasExtension && acceptsHtml);

          if (isRootOrSpaRoute) {
            void (async () => {
              try {
                const currentManifest = await manifestCache.getCachedManifest();
                const packageJsonPath = path.join(resolvedAppDir, "package.json");
                const packageJson = fs.readFileSync(packageJsonPath, "utf8");
                const html = generateIndexHtml({ manifest: currentManifest, packageJson, options });

                const transformedHtml = await timeAsync(`server.transformIndexHtml(${shortenPath(url)})`, () =>
                  server.transformIndexHtml(req.url ?? "/", html)
                );

                res.setHeader("Content-Type", "text/html");
                res.end(transformedHtml);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Error generating index.html:", error);
                res.statusCode = 500;
                res.end(String(error));
              }
            })();
            return; // Don't call next() - we're handling this route
          }
        }
        next();
      });

      // Log success message when server starts
      server.httpServer?.once("listening", () => {
        logEvent("server: listening");
        setTimeout(() => {
          logInfo(
            `\n✨ Iris App is now started, check it out ✨\nhttps://new.manager.trackunit.com/goto/iris-app-dev\n`
          );
        }, 100);
      });
    },

    generateBundle() {
      const manifest = manifestCache.getManifestSync() ?? manifestCache.reloadManifestSync();

      const packageJsonPath = path.join(resolvedAppDir, "package.json");
      const packageJson = fs.readFileSync(packageJsonPath, "utf8");

      // Generate and emit manifest.json
      const manifestJson = generateManifestJson(manifest);
      this.emitFile({
        type: "asset",
        fileName: "manifest.json",
        source: JSON.stringify(manifestJson, null, 2),
      });

      // Emit package.json
      this.emitFile({
        type: "asset",
        fileName: "package.json",
        source: packageJson,
      });

      const html = generateIndexHtml({ manifest, packageJson, options });
      this.emitFile({
        type: "asset",
        fileName: "index.html",
        source: html,
      });
    },

    closeBundle() {
      if (config.command === "build") {
        const manifestForCopy = manifestCache.getManifestSync() ?? manifestCache.reloadManifestSync();
        const copyPatterns = getCopyPatterns({
          nxRootDir: workspaceRoot,
          appDir: resolvedAppDir,
          manifest: manifestForCopy,
          mode: "production",
        });

        const nonExistingPatterns: Array<CopyPattern> = [];
        for (const pattern of copyPatterns) {
          const exists = fs.existsSync(pattern.from);
          if (!exists) {
            nonExistingPatterns.push(pattern);
          }
        }
        if (nonExistingPatterns.length > 0) {
          throw new Error(`Copy patterns do not exist: ${nonExistingPatterns.map(p => p.from).join(", ")}`);
        }

        for (const pattern of copyPatterns) {
          try {
            if (fs.existsSync(pattern.from)) {
              const stat = fs.statSync(pattern.from);
              if (stat.isDirectory()) {
                // Copy directory recursively
                fs.cpSync(pattern.from, pattern.to, { recursive: true });
              } else {
                // Copy single file
                const targetDir = path.dirname(pattern.to);
                if (!fs.existsSync(targetDir)) {
                  fs.mkdirSync(targetDir, { recursive: true });
                }
                fs.copyFileSync(pattern.from, pattern.to);
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error: Could not copy ${pattern.from} to ${pattern.to}:`, error);
            throw error;
          }
        }
      }
    },
  };

  // Return array of plugins:
  // 1. nxViteTsPaths - handles tsconfig path resolution
  // 2. disableHostAutoInitPlugin - intercepts hostAutoInit virtual module (must be before federation)
  // 3. federationPlugin - Module Federation for micro-frontends
  // 4. irisAppPlugin - Iris App specific handling
  // 5. cssInjectedByJsPlugin - Vite equivalent of rspack's "style-loader"
  //    Injects CSS via JS as inline <style> tags instead of external CSS files
  // 6. bundleAnalyzerPlugin - optional bundle analysis
  //
  // NOTE: Tailwind CSS is configured via PostCSS (in baseConfig.css.postcss above)
  // NOT using @tailwindcss/vite because it doesn't respect tailwind.config.js content paths
  // PostCSS approach matches Rspack and properly uses Tailwind v4 config files.
  const plugins: Array<PluginOption> = [
    nxViteTsPaths(),
    // React Fast Refresh — enables HMR for React components so file changes
    // are hot-swapped without a full page reload. Safe with Module Federation
    // because Iris apps bundle their own React (iframe isolation).
    react({}),
    hasExposes && disableHostAutoInitPlugin,
    federationPlugin,
    !options.skipTypeChecking &&
      checker({ typescript: { tsconfigPath: path.join(resolvedAppDir, "tsconfig.app.json") } }),
    irisAppPlugin,
    // Equivalent to rspack's style-loader - injects CSS as inline <style> tags
    // topExecutionPriority ensures CSS is injected before other code runs,
    // relativeCSSInjection: true ensures CSS is injected per chunk (requires cssCodeSplit: true)
    // This is ideal for Module Federation - CSS loads with each remote chunk
    cssInjectedByJsPlugin({
      topExecutionPriority: true,
      relativeCSSInjection: true,
    }),
    bundleAnalyzerPlugin,
    // When the custom-field in-sync check could not be completed, keep reminding
    // the developer on every rebuild that their custom fields were not validated.
    options.degraded && createCustomFieldsDegradedReminderPlugin(true),
  ];
  logTiming(performance.now() - pluginConstructionStart, "plugin construction: complete");
  return plugins.filter(Boolean);
}
